import { instance as boompayClient } from "api/apiBase";
import { inboxClient } from "../../inbox/api/client";
import type {
  Mailbox,
  MailboxPropertyDefaults,
  AssignPropertyDefaultsResponse,
  UpdateMailboxRequest,
  SmsTemplate,
  SmsVariable,
  SmsTemplateListResponse,
  SmsTemplatePayload,
  EmailTemplate,
  EmailVariable,
  PaginatedEmailTemplates,
  CreateEmailTemplatePayload,
  UpdateEmailTemplatePayload
} from "./types";

export const mailboxesApi = {
  list: async () => {
    const { data } = await inboxClient.get<Mailbox[]>("/api/portal/mailboxes");
    return data;
  },

  get: async (id: string) => {
    const { data } = await inboxClient.get<Mailbox>(
      `/api/portal/mailboxes/${id}`
    );
    return data;
  },

  update: async (id: string, payload: UpdateMailboxRequest) => {
    const { data } = await boompayClient.patch<Mailbox>(
      `/portal/v1/communication_mailboxes/${id}`,
      payload
    );
    return data;
  },

  // CRM-1165: "default" is a per-property assignment. The PUT has REPLACE
  // semantics — property_ids is the mailbox's whole assignment, [] clears it.
  getPropertyDefaults: async (mailboxId: string) => {
    const { data } = await boompayClient.get<MailboxPropertyDefaults>(
      `/portal/v1/communication_mailboxes/${mailboxId}/property_defaults`
    );
    return data;
  },

  setPropertyDefaults: async (mailboxId: string, propertyIds: number[]) => {
    const { data } = await boompayClient.put<AssignPropertyDefaultsResponse>(
      `/portal/v1/communication_mailboxes/${mailboxId}/property_defaults`,
      { property_ids: propertyIds }
    );
    return data;
  },

  setPropertyDefault: async (propertyId: string, mailboxId: string) => {
    await boompayClient.put(
      `/portal/v1/communication_mailboxes/properties/${propertyId}/default`,
      { mailbox_id: mailboxId }
    );
  },

  removePropertyDefault: async (propertyId: string) => {
    await boompayClient.delete(
      `/portal/v1/communication_mailboxes/properties/${propertyId}/default`
    );
  },

  disconnect: async (id: string) => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/${id}`);
  },

  subscribe: async (id: string) => {
    await boompayClient.post(`/portal/v1/communication_mailboxes/${id}/sync`);
  },

  unsubscribe: async (id: string) => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/${id}/sync`);
  }
};

const SMS_TEMPLATES_BASE = "/portal/v1/boom_crm/sms_templates";

export const smsTemplatesApi = {
  list: async (page = 1, perPage = 100, search?: string) => {
    const { data } = await boompayClient.get<SmsTemplateListResponse>(
      SMS_TEMPLATES_BASE,
      { params: { page, per_page: perPage, ...(search ? { search } : {}) } }
    );
    return data;
  },

  get: async (id: number) => {
    const { data } = await boompayClient.get<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}`
    );
    return data;
  },

  create: async (payload: SmsTemplatePayload) => {
    const { data } = await boompayClient.post<SmsTemplate>(
      SMS_TEMPLATES_BASE,
      payload
    );
    return data;
  },

  update: async (id: number, payload: Partial<SmsTemplatePayload>) => {
    const { data } = await boompayClient.patch<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}`,
      payload
    );
    return data;
  },

  duplicate: async (id: number) => {
    const { data } = await boompayClient.post<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}/duplicate`
    );
    return data;
  },

  delete: async (id: number) => {
    await boompayClient.delete(`${SMS_TEMPLATES_BASE}/${id}`);
  },

  variables: async (subjectType = "lead") => {
    const { data } = await boompayClient.get<{ variables: SmsVariable[] }>(
      `${SMS_TEMPLATES_BASE}/variables`,
      { params: { subject_type: subjectType } }
    );
    return data.variables;
  },

  render: async (
    id: number,
    contactId: number,
    options?: { leadId?: number; toNumber?: string }
  ) => {
    const { data } = await boompayClient.post<{
      rendered_body: string;
      variable_values: Record<string, string>;
    }>(`${SMS_TEMPLATES_BASE}/${id}/render`, {
      contact_id: contactId,
      lead_id: options?.leadId,
      to_number: options?.toNumber
    });
    return data;
  }
};

const EMAIL_TEMPLATES_PATH = "/portal/v1/boom_crm/email_templates";

export const emailTemplatesApi = {
  list: async (
    params?: Record<string, unknown>
  ): Promise<PaginatedEmailTemplates> => {
    const { data } = await boompayClient.get(EMAIL_TEMPLATES_PATH, { params });
    return data;
  },

  get: async (id: number): Promise<EmailTemplate> => {
    const { data } = await boompayClient.get(`${EMAIL_TEMPLATES_PATH}/${id}`);
    return data;
  },

  create: async (
    payload: CreateEmailTemplatePayload
  ): Promise<EmailTemplate> => {
    const { data } = await boompayClient.post(EMAIL_TEMPLATES_PATH, payload);
    return data;
  },

  update: async (
    id: number,
    payload: UpdateEmailTemplatePayload
  ): Promise<EmailTemplate> => {
    const { data } = await boompayClient.patch(
      `${EMAIL_TEMPLATES_PATH}/${id}`,
      payload
    );
    return data;
  },
  duplicate: async (id: number): Promise<EmailTemplate> => {
    const { data } = await boompayClient.post(
      `${EMAIL_TEMPLATES_PATH}/${id}/duplicate`
    );
    return data;
  },

  delete: async (id: number) =>
    await boompayClient.delete(`${EMAIL_TEMPLATES_PATH}/${id}`),

  variables: async (subjectType = "lead") => {
    const { data } = await boompayClient.get<{ variables: EmailVariable[] }>(
      `${EMAIL_TEMPLATES_PATH}/variables`,
      { params: { subject_type: subjectType } }
    );
    return data.variables;
  },

  // Backend endpoint not yet implemented — stub logs payload until the
  // Ruby side adds POST /portal/v1/boom_crm/email_templates/:id/send_test.
  sendTest: async (id: number, payload: { emails: string[] }) => {
    // eslint-disable-next-line no-console -- placeholder until backend route lands
    console.log("sendTest stub", { id, payload });
  }
};
